import type { AdminIntegrationVisibilityRow, IntegrationVisibility } from '@/api/adminClient';
import type { StatusTone } from '@/lib/runTelemetry';

/** The two choices a RapidSOS admin has; an organization's own connector is set where it is built. */
export const VISIBILITY_OPTIONS: Array<{ id: 'everyone' | 'assigned'; label: string; description: string }> = [
  {
    id: 'everyone',
    label: 'Everyone',
    description: 'Every organization can find and use it. It is on for them unless they turn it off.',
  },
  {
    id: 'assigned',
    label: 'Specific organizations',
    description: 'Only the organizations listed here can find and use it. It is on for them unless they turn it off.',
  },
];

export function integrationKindLabel(kind: AdminIntegrationVisibilityRow['kind']): string {
  return kind === 'catalog' ? 'Catalog' : 'Builder';
}

/** "Everyone", "3 organizations", "Only Bee County", "Converting…": the pill beside an integration. */
export function visibilitySummary(
  row: Pick<AdminIntegrationVisibilityRow, 'visibility' | 'ownerOrganizationId' | 'organizationCount'>,
  organizationName: (organizationId: string) => string = (organizationId) => organizationId,
): { label: string; tone: StatusTone } {
  if (row.visibility === null) return { label: 'Converting…', tone: 'neutral' };
  if (row.visibility === 'everyone') return { label: 'Everyone', tone: 'success' };
  if (row.visibility === 'organization') {
    return { label: `Only ${row.ownerOrganizationId ? organizationName(row.ownerOrganizationId) : 'its organization'}`, tone: 'neutral' };
  }
  const count = row.organizationCount;
  if (count === undefined) return { label: 'Specific organizations', tone: 'warning' };
  return { label: count === 1 ? '1 organization' : `${count} organizations`, tone: count === 0 ? 'warning' : 'neutral' };
}

/** Who a release reaches, for the Integration Builder's copy. */
export function releaseAudience(visibility: IntegrationVisibility | undefined): string {
  if (visibility === 'everyone') return 'every organization';
  if (visibility === 'assigned') return 'the organizations it is assigned to';
  if (visibility === 'organization') return 'the organization that owns it';
  return 'whoever can use it';
}

/** Integrations an organization could be given: assigned-only ones it does not have yet. */
export function assignableIntegrations(rows: AdminIntegrationVisibilityRow[], assignedIds: Iterable<string>): AdminIntegrationVisibilityRow[] {
  const taken = new Set(assignedIds);
  return rows.filter((row) => row.visibility === 'assigned' && !taken.has(row.integrationId));
}

export function integrationPath(integrationId: string): string {
  return `/integrations/${encodeURIComponent(integrationId)}`;
}

export type VisibilityFilter = 'all' | 'everyone' | 'assigned' | 'organization' | 'converting';

export const VISIBILITY_FILTERS: Array<{ id: VisibilityFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'everyone', label: 'Everyone' },
  { id: 'assigned', label: 'Specific organizations' },
  { id: 'organization', label: 'One organization' },
  { id: 'converting', label: 'Converting' },
];

/** The rows matching the search (title or id) and the visibility filter. */
export function filterIntegrationRows(rows: AdminIntegrationVisibilityRow[], query: string, filter: VisibilityFilter): AdminIntegrationVisibilityRow[] {
  const needle = query.trim().toLowerCase();
  return rows
    .filter((row) => (filter === 'all' ? true : filter === 'converting' ? row.visibility === null : row.visibility === filter))
    .filter((row) => !needle || `${row.title} ${row.integrationId}`.toLowerCase().includes(needle));
}
